import { RESELL_RIGHTS_PURPOSE } from './catalog-taxonomy';
import { aiUseCopyOf, showsAiLabel } from './ai-disclosure';

/**
 * The labels beside a product's name, each of which a buyer can open for what
 * it means (owner, 2026-11-02): Resell rights on a resale pack, so a buyer
 * expecting original work knows other sellers may offer the same content, and
 * AI-assisted when the seller says AI helped (ai-disclosure.ts). Drawn by
 * ListingBadges (src/components/product/listing-badges.tsx), which opens the
 * meaning on a tap as well as a click.
 */

export type ListingBadgeKind = 'resell-rights' | 'ai-assisted';

export type ListingBadge = {
  kind: ListingBadgeKind;
  label: string;
  /** What the label means. */
  detail: string;
  /** What else a buyer should know before buying. */
  note: string;
  link: { href: string; text: string } | null;
};

/** Whether a product with these purposes is a resale pack: Resell rights is then its only purpose. */
export function isResalePack(purposes: readonly (string | null | undefined)[]): boolean {
  return purposes.includes(RESELL_RIGHTS_PURPOSE);
}

const RESELL_RIGHTS_BADGE: ListingBadge = {
  kind: 'resell-rights',
  label: 'Resell rights',
  detail: 'A resale pack (PLR or MRR): buying it gives you rights to resell or rebrand what is inside, on the terms of its licence.',
  note: 'The seller may not have made it, and other sellers can offer the same content, here and elsewhere. Check the description for what the licence allows.',
  link: null,
};

/**
 * The badges a product shows, in order. On the product page `purposes` are
 * the effective ones (effectiveTaxonomyOf), so the badge agrees with the
 * page's Purpose row and the marketplace's shelf; the editors' previews pass
 * the form's two purpose fields, either of which may be empty.
 */
export function listingBadgesOf({ aiUse, purposes }: { aiUse: unknown; purposes: readonly (string | null | undefined)[] }): ListingBadge[] {
  const resalePack = isResalePack(purposes);
  const badges: ListingBadge[] = [];
  if (resalePack) badges.push(RESELL_RIGHTS_BADGE);
  if (showsAiLabel(aiUse)) {
    const copy = aiUseCopyOf(resalePack).assisted;
    badges.push({
      kind: 'ai-assisted',
      label: copy.label,
      detail: copy.detail,
      note: 'Products generated by AI are not allowed on supa.place.',
      link: { href: '/guidelines#ai-generated-content-disclosure', text: 'Our rules on AI' },
    });
  }
  return badges;
}
